import React, { useMemo, useState, useEffect } from 'react';
import { DataGridPro, GridActionsCellItem, GridColDef, GridToolbar, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { MediaFile } from 'types/files';
import useLocalization from 'hooks/useLocalization';
import { useTableLocaleText } from 'hooks/useTableLocaleText';
import { Box } from '@mui/material';

interface FilesTableViewProps {
  files: MediaFile[];
  handleDeleteFileFromGallery: (file: MediaFile) => Promise<void>;
  onEditExistingNameClick: (file: MediaFile) => void;
  onScrollEndReached: () => void;
  noActions?: boolean;
  onClickAction?: (file: MediaFile) => void;
}

const FilesTableView: React.FC<FilesTableViewProps> = ({
  files,
  handleDeleteFileFromGallery,
  onEditExistingNameClick,
  onScrollEndReached,
  noActions,
  onClickAction,
}) => {
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => currentLocale.settingsPopup.uploadFiles, [currentLocale]);

  const tableLocaleText = useTableLocaleText();

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    actions: !noActions,
  });

  // Update actions column visibility when noActions prop changes
  useEffect(() => {
    setColumnVisibilityModel(prev => ({
      ...prev,
      actions: !noActions,
    }));
  }, [noActions]);

  const columns = useMemo<GridColDef<MediaFile>[]>(() => {
    return [
      {
        field: 'Name',
        headerName: localization.fileName,
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'DateCreate',
        headerName: localization.lastModified,
        minWidth: 280,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'פעולות',
        width: 120,
        getActions: ({ row }: { row: MediaFile }) => [
          <GridActionsCellItem
            onClick={() => handleDeleteFileFromGallery(row)}
            label={localization.menu.delete}
            showInMenu
          />,
          <GridActionsCellItem
            onClick={() => onEditExistingNameClick(row)}
            label={localization.menu.editName}
            showInMenu
          />,
        ],
      },
    ];
  }, [
    handleDeleteFileFromGallery,
    localization.fileName,
    localization.lastModified,
    localization.menu.delete,
    localization.menu.editName,
    onEditExistingNameClick,
  ]);

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <DataGridPro
        rows={files}
        columns={columns}
        getRowId={(row) => row.Id}
        slots={{ toolbar: GridToolbar }}
        localeText={tableLocaleText}
        onRowClick={(params, event, details) => onClickAction && onClickAction(params.row)}
        onRowsScrollEnd={onScrollEndReached}
        scrollEndThreshold={200}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        disableColumnResize
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          width: '100%',
        }}
      />
    </Box>
  );
};

export default FilesTableView;
